import { Footer } from "@excalidraw/excalidraw/index";
import { exportToSvg } from "@excalidraw/utils";
import { CaptureUpdateAction } from "@excalidraw/element";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import React from "react";

import { isExcalidrawPlusSignedUser } from "../app_constants";
// @ts-ignore — vendored zero-dep ESM, allowJs is on
import { detectOuterCircle } from "../witchhat/outer-circle.js";

import { DebugFooter, isVisualDebuggerEnabled } from "./DebugCanvas";
import { EncryptedIcon } from "./EncryptedIcon";

// Random 31-bit positive int — same shape as excalidraw's randomInteger().
const randomVersionNonce = () =>
  Math.floor(Math.random() * 0x7fffffff);

type Props = {
  onChange: () => void;
  excalidrawAPI?: ExcalidrawImperativeAPI | null;
};

const ORANGE = "#ff8c00";

// Walk the rendered cast SVG, find each <g data-id="…"> (excalidraw tags
// every element's group with this), and decide which ones lie on the
// detected outer circle. Returns a Set of element IDs.
//
// Works by mounting the SVG element off-screen so getBBox()/getCTM() return
// real user-space coordinates that include any group transforms excalidraw
// applies (translate, scale, rotate). Then for each tagged group we sample
// its bbox corners + center, transform them into root-SVG user space via
// the CTM, and ask: "is most of this point set within the inlier band of
// the detected circle?"
function findOuterRingElementIds(
  svgElement: SVGSVGElement,
  detection: any,
): Set<string> {
  const ids = new Set<string>();
  if (detection.error || detection.radius === 0) {
    console.warn("[witchhat:find] skipped — no detection or zero radius");
    return ids;
  }

  // Mount the cloned SVG at its NATURAL size (no width/height override).
  // Crucial: if we constrain to width:1px;height:1px the entire viewBox
  // gets scaled to 1px, and getCTM returns a degenerate near-zero scale
  // that maps every element onto a single point in viewport space.
  // Just position it way off-screen and hide via opacity.
  const cloned = svgElement.cloneNode(true) as SVGSVGElement;
  cloned.style.cssText = [
    "position:fixed",
    "left:-99999px",
    "top:-99999px",
    "pointer-events:none",
    "opacity:0.001",
    "z-index:-1",
  ].join(";");
  document.body.appendChild(cloned);

  try {
    // Try multiple selectors — data-id should now land on <g>, but in case
    // it landed on <path> or somewhere else in the rendering tree.
    const groupsG = cloned.querySelectorAll<SVGGraphicsElement>("g[data-id]");
    const groupsAny = cloned.querySelectorAll<SVGGraphicsElement>("[data-id]");
    console.log(
      `[witchhat:find] selectors: g[data-id]=${groupsG.length} [data-id]=${groupsAny.length}`,
    );
    const groups = groupsG.length > 0 ? groupsG : groupsAny;
    if (groups.length === 0) {
      console.warn("[witchhat:find] no [data-id] elements found at all — patch may not be in this bundle");
      return ids;
    }

    const cx = detection.center.x;
    const cy = detection.center.y;
    const r = detection.radius;
    const band = Math.max(r * 0.18, 8);
    console.log(
      `[witchhat:find] target: center=(${cx.toFixed(1)},${cy.toFixed(1)}) r=${r.toFixed(1)} band=±${band.toFixed(1)}`,
    );

    let inspected = 0;
    let bboxFails = 0;
    let ctmFails = 0;

    for (const g of Array.from(groups)) {
      const id = g.getAttribute("data-id");
      if (!id) continue;
      inspected++;

      let bbox: SVGRect;
      try {
        bbox = g.getBBox();
      } catch (err) {
        bboxFails++;
        console.warn(`[witchhat:find] getBBox threw for ${id}:`, err);
        continue;
      }
      if (!Number.isFinite(bbox.width) || !Number.isFinite(bbox.height)) {
        bboxFails++;
        continue;
      }

      const ctm = g.getCTM();
      if (!ctm) {
        ctmFails++;
        continue;
      }

      const samples = [
        { x: bbox.x, y: bbox.y },
        { x: bbox.x + bbox.width / 2, y: bbox.y },
        { x: bbox.x + bbox.width, y: bbox.y },
        { x: bbox.x, y: bbox.y + bbox.height / 2 },
        { x: bbox.x + bbox.width / 2, y: bbox.y + bbox.height / 2 },
        { x: bbox.x + bbox.width, y: bbox.y + bbox.height / 2 },
        { x: bbox.x, y: bbox.y + bbox.height },
        { x: bbox.x + bbox.width / 2, y: bbox.y + bbox.height },
        { x: bbox.x + bbox.width, y: bbox.y + bbox.height },
      ];

      // ctm transforms element-local → svg-viewport. We want element-local →
      // root-svg user space. Use getCTM relative to the svg root via the
      // svg's inverse screen-CTM trick:
      //   userSpace = ctm * point  (when CTM is local→viewport == local→root for our case)
      // For excalidraw exports the SVG root has identity screenCTM mostly, so
      // ctm * sample IS in root user-space.
      const transformed = samples.map((p) => ({
        x: ctm.a * p.x + ctm.c * p.y + ctm.e,
        y: ctm.b * p.x + ctm.d * p.y + ctm.f,
      }));

      const candidates =
        bbox.width < 5 && bbox.height < 5
          ? [transformed[4]]
          : transformed;

      let onCircle = 0;
      const dists: number[] = [];
      for (const p of candidates) {
        const d = Math.hypot(p.x - cx, p.y - cy);
        dists.push(d);
        if (Math.abs(d - r) < band) onCircle++;
      }

      const matched = candidates.length === 1
        ? onCircle === 1
        : onCircle >= Math.ceil(candidates.length * 0.4);

      console.log(
        `[witchhat:find] ${id.slice(0, 8)}: bbox=(${bbox.x.toFixed(0)},${bbox.y.toFixed(0)},${bbox.width.toFixed(0)}x${bbox.height.toFixed(0)}) ctm=[${ctm.a.toFixed(2)},${ctm.b.toFixed(2)},${ctm.c.toFixed(2)},${ctm.d.toFixed(2)},${ctm.e.toFixed(1)},${ctm.f.toFixed(1)}] dists=[${dists.map(d => d.toFixed(0)).join(",")}] onCircle=${onCircle}/${candidates.length} → ${matched ? "✓" : "✗"}`,
      );

      if (matched) ids.add(id);
    }

    console.log(
      `[witchhat:find] inspected=${inspected} bboxFails=${bboxFails} ctmFails=${ctmFails} matched=${ids.size}`,
    );
  } finally {
    cloned.remove();
  }

  return ids;
}

// Build an orange overlay (every detected ring + center cross) and merge it
// into the cast SVG so the user can see what the algorithm picked. Inner
// rings get progressively lighter so they're distinguishable from the outer.
function augmentSvgWithOrangeOverlay(
  svgString: string,
  detection: any,
): string {
  if (detection.error || detection.radius === 0) return svgString;
  const rings = (detection.allRings && detection.allRings.length > 0)
    ? detection.allRings
    : [{ center: detection.center, radius: detection.radius }];

  const overlay: string[] = [`<g data-witchhat-overlay="rings" pointer-events="none">`];
  rings.forEach((ring: any, i: number) => {
    // Outermost = solid bright orange. Inner rings get more transparent.
    const opacity = Math.max(0.4, 0.95 - i * 0.2);
    const dash = i === 0 ? "6 4" : `${4 + i} ${3 + i}`;
    overlay.push(
      `<circle cx="${ring.center.x}" cy="${ring.center.y}" r="${ring.radius}" fill="none" stroke="#ff8c00" stroke-width="${i === 0 ? 3 : 2}" stroke-dasharray="${dash}" opacity="${opacity}"/>`,
    );
  });
  // Center crosshair on the outermost
  const { center } = detection;
  overlay.push(
    `<line x1="${center.x - 8}" y1="${center.y}" x2="${center.x + 8}" y2="${center.y}" stroke="#ff8c00" stroke-width="2"/>`,
    `<line x1="${center.x}" y1="${center.y - 8}" x2="${center.x}" y2="${center.y + 8}" stroke="#ff8c00" stroke-width="2"/>`,
    `<circle cx="${center.x}" cy="${center.y}" r="2" fill="#ff8c00"/>`,
    `</g>`,
  );
  return svgString.replace(/<\/svg>/i, `${overlay.join("")}</svg>`);
}

// Show the cast result in a modal <dialog>: original SVG with orange overlay
// of the detected outer circle, plus stats. "OK" closes.
function showCastDialog(
  svgString: string,
  detection: any,
  selectionCount: number,
  outerRingElementCount: number = 0,
) {
  // Tear down any existing dialog first
  document.querySelectorAll('[data-witchhat-cast-dialog]').forEach((el) => el.remove());

  const dialog = document.createElement("dialog");
  dialog.setAttribute("data-witchhat-cast-dialog", "1");
  dialog.style.cssText = [
    "border: 1px solid rgba(184,160,107,0.6)",
    "border-radius: 0.65rem",
    "background: linear-gradient(180deg,#fbf6e8,#f1e7c9)",
    "color: #3a2f1c",
    "padding: 1.25rem 1.5rem",
    "max-width: 720px",
    "min-width: 320px",
    "font-family: MedievalSharp, Cinzel Decorative, Cardo, Cinzel, Georgia, serif",
    "box-shadow: 0 8px 32px rgba(80,60,30,0.25)",
  ].join(";");

  const augmentedSvg = augmentSvgWithOrangeOverlay(svgString, detection);
  const verdict = detection.error
    ? `(no circle detected: ${detection.error})`
    : detection.isCircle
      ? "🟠 detected as a circle"
      : "❌ not really a circle";
  const recolorMsg = outerRingElementCount > 0
    ? `🪄 recolored <b>${outerRingElementCount}</b> element(s) on the canvas to orange`
    : "<i>no canvas elements matched the detected circle (try drawing a clearer outer ring)</i>";

  const ringRows = (detection.allRings ?? [])
    .map((ring: any, i: number) => {
      const label = i === 0 ? "outermost" : i === detection.allRings.length - 1 ? "innermost" : `ring ${i}`;
      return `<tr>
        <td style="padding-right: 1rem; color:#7a5c2c">${label}</td>
        <td>r=${ring.radius.toFixed(1)} · center=(${ring.center.x.toFixed(0)}, ${ring.center.y.toFixed(0)}) · circ=${ring.circularity.toFixed(3)} · subpaths=[${ring.inlierSubpathIndices.join(",")}]</td>
      </tr>`;
    })
    .join("");
  const stats = detection.error ? "" : `
    <table style="font-family: ui-monospace, monospace; font-size: 0.85rem; line-height: 1.5; border-collapse: collapse;">
      <tr><td style="padding-right: 1rem; color:#7a5c2c">rings found</td><td><b>${detection.ringCount ?? 1}</b></td></tr>
      ${ringRows}
      <tr><td style="padding-right: 1rem; color:#7a5c2c">remaining subpaths</td><td>[${(detection.remainingSubpathIndices ?? []).join(", ")}] (candidate symbols inside the rings)</td></tr>
      <tr><td colspan="2" style="padding-top:0.4rem; border-top:1px solid rgba(184,160,107,0.3)"></td></tr>
      <tr><td style="padding-right: 1rem; color:#7a5c2c">residual fit</td><td>${detection.residualCircularity.toFixed(3)} (rmse=${detection.rmse.toFixed(2)})</td></tr>
      <tr><td style="padding-right: 1rem; color:#7a5c2c">isoperimetric</td><td>${detection.isoperimetricRatio.toFixed(3)}</td></tr>
      <tr><td style="padding-right: 1rem; color:#7a5c2c">inliers</td><td>${detection.inlierCount}/${detection.totalPointCount} points</td></tr>
      ${detection.fallback ? `<tr><td colspan="2" style="color:#a04020; padding-top:0.5rem"><i>(fallback: RANSAC found no circle; showing largest-bbox subpath)</i></td></tr>` : ""}
    </table>
  `;

  dialog.innerHTML = `
    <div style="display:flex; flex-direction:column; gap: 0.9rem; align-items:stretch;">
      <div style="font-size: 1.4rem; letter-spacing: 0.08em;">✦ Cast — ${selectionCount} element(s)</div>
      <div style="font-size: 1.05rem;">${verdict}</div>
      <div style="font-size: 0.95rem; color:#7a5c2c;">${recolorMsg}</div>
      <div style="background: #fff; border: 1px solid rgba(184,160,107,0.4); border-radius: 0.4rem; padding: 0.5rem; max-width: 100%; overflow: auto;">
        <div style="display:flex; justify-content:center;">
          <div style="max-width: 100%; max-height: 380px; overflow:auto;">${augmentedSvg}</div>
        </div>
      </div>
      <div style="font-size: 0.8rem; color:#7a5c2c;">↑ orange dashed circle = detected outer ring · cross = center</div>
      ${stats}
      <div style="display:flex; justify-content:flex-end; margin-top: 0.5rem;">
        <button data-witchhat-ok="1" style="
          padding: 0.5rem 1.2rem;
          border: 1px solid rgba(184,160,107,0.6);
          border-radius: 0.5rem;
          background: linear-gradient(180deg,#e8d59a,#c8a866);
          color: #2a2010;
          font-family: inherit;
          font-size: 1rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          cursor: pointer;
        ">OK</button>
      </div>
    </div>
  `;

  // Cap the embedded SVG so it fits the modal
  const innerSvg = dialog.querySelector("svg");
  if (innerSvg) {
    innerSvg.style.maxWidth = "100%";
    innerSvg.style.maxHeight = "360px";
    innerSvg.style.height = "auto";
  }

  dialog.querySelector<HTMLButtonElement>("[data-witchhat-ok]")?.addEventListener("click", () => dialog.close());
  dialog.addEventListener("close", () => dialog.remove());
  document.body.appendChild(dialog);
  dialog.showModal();
}

export const AppFooter = React.memo(({ onChange, excalidrawAPI }: Props) => {
  const onCast = React.useCallback(async () => {
    if (!excalidrawAPI) {
      console.warn("[witchhat:cast] no excalidrawAPI");
      return;
    }
    const appState = excalidrawAPI.getAppState();
    const allElements = excalidrawAPI.getSceneElements();
    const selectedIds = appState.selectedElementIds || {};
    const selected = allElements.filter((el) => selectedIds[el.id]);

    if (selected.length === 0) {
      console.log(
        "%c[witchhat:cast] no selection — pick elements with the selection tool first",
        "color:#b8a06b;font-weight:bold",
      );
      return;
    }

    try {
      const files = excalidrawAPI.getFiles();
      const svg = await exportToSvg({
        elements: selected,
        appState,
        files,
        exportPadding: 16,
      });

      const svgString = svg.outerHTML;
      console.groupCollapsed(
        `%c[witchhat:cast] cast complete — ${selected.length} element(s)`,
        "color:#b8a06b;font-weight:bold",
      );
      console.log(
        "%cselected element types:",
        "color:#9d8d6c",
        selected.map((el) => `${el.type}#${el.id.slice(0, 6)}`),
      );
      console.log("%cSVG length:", "color:#9d8d6c", svgString.length);
      console.log(
        "%cSVG markup (full):",
        "color:#9d8d6c",
        "\n" + svgString,
      );
      console.log("%cSVGSVGElement object:", "color:#9d8d6c", svg);

      // Run outer-circle detection on the cast SVG.
      const detection = detectOuterCircle(svgString);
      console.log(
        "%c[witchhat:cast] circle detection:",
        "color:#7a5cff;font-weight:bold",
        detection,
      );

      // Map detected geometry back to excalidraw element IDs by walking the
      // rendered SVG's <g data-id> groups and checking which lie on the
      // detected circle in user-space.
      const outerRingIds = findOuterRingElementIds(svg, detection);
      console.log(
        "%c[witchhat:cast] outer-ring element IDs:",
        "color:#ff8c00;font-weight:bold",
        Array.from(outerRingIds),
      );
      console.groupEnd();

      // Mutate the canvas: recolor matched elements' strokes orange.
      // CRITICAL: excalidraw uses (version, versionNonce, updated) for change
      // detection — without bumping these, the change is silently reverted
      // on the next render. Also pass captureUpdate: IMMEDIATELY so the
      // change lands in the undo stack and persists across re-renders.
      const originalColors = new Map<string, string>();
      if (outerRingIds.size > 0) {
        const updatedElements = allElements.map((el) => {
          if (!outerRingIds.has(el.id)) return el;
          originalColors.set(el.id, el.strokeColor);
          return {
            ...el,
            strokeColor: ORANGE,
            version: el.version + 1,
            versionNonce: randomVersionNonce(),
            updated: Date.now(),
          };
        });
        excalidrawAPI.updateScene({
          elements: updatedElements,
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });
        console.log(
          "%c[witchhat:cast] recolored:",
          "color:#ff8c00;font-weight:bold",
          { count: outerRingIds.size, ids: Array.from(outerRingIds) },
        );
      } else {
        console.warn(
          "%c[witchhat:cast] no element IDs matched the detected circle — check that data-id is being set on element groups",
          "color:#ff6b6b",
        );
      }

      // Stash on window for easy poking from devtools
      (window as any).__witchhatLastCast = {
        elements: selected,
        svg: svgString,
        svgElement: svg,
        detection,
        outerRingIds: Array.from(outerRingIds),
        originalColors: Object.fromEntries(originalColors),
        at: new Date().toISOString(),
      };

      // Modal popup with the cast SVG + orange overlay
      showCastDialog(svgString, detection, selected.length, outerRingIds.size);
    } catch (err) {
      console.error("[witchhat:cast] export failed:", err);
    }
  }, [excalidrawAPI]);

  return (
    <Footer>
      <div
        style={{
          display: "flex",
          gap: ".5rem",
          alignItems: "center",
        }}
      >
        {isVisualDebuggerEnabled() && <DebugFooter onChange={onChange} />}
        {!isExcalidrawPlusSignedUser && <EncryptedIcon />}
        <button
          type="button"
          onClick={onCast}
          title="Cast — log selected SVGs to console"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.4rem",
            padding: "0.45rem 0.9rem",
            border: "1px solid rgba(184, 160, 107, 0.55)",
            borderRadius: "0.65rem",
            background:
              "linear-gradient(180deg, rgba(184, 160, 107, 0.15), rgba(80, 60, 30, 0.08))",
            color: "#3a2f1c",
            fontFamily:
              "MedievalSharp, Cinzel Decorative, Cardo, Cinzel, Georgia, serif",
            fontSize: "0.95rem",
            fontWeight: 600,
            letterSpacing: "0.06em",
            cursor: "pointer",
            boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
          }}
        >
          ✦ Cast
        </button>
      </div>
    </Footer>
  );
});
